
import { Menu as VMenu } from 'floating-vue'
import { defineComponent, PropType } from 'vue'

import { IgnoreCheckIssueActionChoice } from '~/types/types'
import { UiIssueLevel } from '~/utils/issue'
import { createPopperScrollGuard, PopperScrollGuard } from '~/utils/popper'
import { prCopyText } from '~/utils/ui'

export type IgnoreContext = 'pr-check' | 'default-branch-check' | 'issues-tab'

type ModalType =
  | ''
  | 'intentional'
  | 'falsePositive'
  | 'occurrence'
  | 'ignoreFile'
  | 'allFilesPr'
  | 'allFilesRepo'
  | 'filePattern'
  | 'testFiles'

export default defineComponent({
  name: 'IssueIgnoreDropdown',
  components: {
    VMenu
  },
  emits: ['ignore', 'menuToggled'],
  props: {
    shortcode: {
      type: String,
      required: true
    },
    checkIssueId: {
      type: String,
      default: ''
    },
    context: {
      type: String as PropType<IgnoreContext>,
      required: true
    },
    checkId: {
      type: String,
      default: ''
    },
    filePath: {
      type: String,
      default: ''
    },
    ignoreActionType: {
      type: String as PropType<IgnoreCheckIssueActionChoice>,
      required: true
    },
    disableFalsePositive: {
      type: Boolean,
      default: false
    },
    forceClose: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: ''
    },
    repositoryId: {
      type: String,
      default: ''
    },
    isLatestRun: {
      type: Boolean,
      default: true
    },
    latestRunUrl: {
      type: String,
      default: ''
    },
    testPatternCount: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      isDropdownOpen: false,
      activeModal: '' as ModalType,
      isModalOpen: false,
      dropdownScrollGuard: null as PopperScrollGuard | null
    }
  },
  created(): void {
    this.dropdownScrollGuard = createPopperScrollGuard(
      () => {
        this.isDropdownOpen = false
      },
      {
        useInitGuard: true
      }
    )
  },
  beforeDestroy(): void {
    this.dropdownScrollGuard?.cleanup()
  },
  watch: {
    forceClose(shouldClose: boolean): void {
      if (shouldClose && this.isDropdownOpen) {
        this.isDropdownOpen = false
      }
    }
  },
  computed: {
    UiIssueLevel() {
      return UiIssueLevel
    },
    isNonLatestPrRun(): boolean {
      return this.context === 'pr-check' && !this.isLatestRun
    },
    prLabel(): string {
      const routeMeta = this.$providerMetaMap[this.$route.params.provider]
      return prCopyText(routeMeta?.shortcode ?? null)
    },
    ignoreRuleCheckId(): string {
      if (this.context === 'pr-check') {
        return this.checkId
      }
      return ''
    }
  },
  methods: {
    openModal(modalType: ModalType): void {
      this.isDropdownOpen = false
      this.activeModal = modalType
      this.isModalOpen = true
    },
    closeModal(): void {
      this.isModalOpen = false
      this.activeModal = ''
    },
    handleIgnore(checkIssueIds: string[]): void {
      this.closeModal()
      this.$emit('ignore', {
        ignoredIssueIds: checkIssueIds,
        checkId: this.checkId
      })
    },
    onDropdownShow(): void {
      this.$emit('menuToggled', { id: this.id, isOpen: true })
      this.dropdownScrollGuard?.onShow()
    },
    onDropdownHide(): void {
      this.$emit('menuToggled', { id: this.id, isOpen: false })
      this.dropdownScrollGuard?.onHide()
    }
  }
})
